import { IlogisticServiceDetailsToolsData } from 'types/services';
import { create } from 'zustand';

export const SECTIONS_STEPS = {
  SELECTION: 'SELECTION',
  CONFIRMATION: 'CONFIRMATION',
  ADD_ADDITIONAL_TOOLS: 'ADD_ADDITIONAL_TOOLS'
} as const;

export type TSectionSteps = typeof SECTIONS_STEPS[keyof typeof SECTIONS_STEPS];

interface DeliveredTool {
  tool: IlogisticServiceDetailsToolsData;
  originalIndex: number;
}

interface IDeliveredToolsState {
  availableTools: IlogisticServiceDetailsToolsData[];
  setAvailableTools: (tools: IlogisticServiceDetailsToolsData[]) => void;
  
  markedNotDeliveredToolIds: number[];
  setMarkedNotDeliveredToolIds: (ids: number[]) => void;
  
  selectedTools: number[];
  setSelectedTools: (selectedTools: number[]) => void;
  addSelectedTool: (index: number) => void;
  removeSelectedTool: (index: number) => void;
  toggleToolSelection: (index: number) => void;
  clearSelectedTools: () => void;
  
  unselectedTools: number[];
  setUnselectedTools: (unselectedTools: number[]) => void;
  
  selectAll: boolean;
  setSelectAll: (selectAll: boolean) => void;
  toggleSelectAll: () => void;
  
  deliveryDate: string;
  setDeliveryDate: (date: string) => void;
  
  currentStep: TSectionSteps;
  setCurrentStep: (step: TSectionSteps) => void;
  
  getSelectedToolsArray: () => DeliveredTool[];
  getUnselectedToolsArray: () => DeliveredTool[];
  isToolSelected: (index: number) => boolean;
  getSelectedToolsCount: () => number;
  
  resetState: () => void;
}

const useDeliveredToolsState = create<IDeliveredToolsState>((set, get) => ({
  availableTools: [],
  markedNotDeliveredToolIds: [],
  selectedTools: [],
  unselectedTools: [],
  selectAll: false,
  deliveryDate: '',
  currentStep: SECTIONS_STEPS.SELECTION,
  
  setAvailableTools: (tools) => {
    const allIndices = tools.map((_, index) => index);
    set({ 
      availableTools: tools,
      unselectedTools: allIndices 
    });
  },
  setMarkedNotDeliveredToolIds: (ids) => set({ markedNotDeliveredToolIds: ids }),
  setSelectedTools: (selectedTools) => {
    const { availableTools } = get();
    const allIndices = availableTools.map((_, index) => index);
    const unselectedTools = allIndices.filter(index => !selectedTools.includes(index));
    set({ 
      selectedTools, 
      unselectedTools,
      selectAll: selectedTools.length === availableTools.length
    });
  },
  setUnselectedTools: (unselectedTools) => {
    const { availableTools } = get();
    const allIndices = availableTools.map((_, index) => index);
    const selectedTools = allIndices.filter(index => !unselectedTools.includes(index));
    set({ 
      unselectedTools, 
      selectedTools,
      selectAll: selectedTools.length === availableTools.length
    });
  },
  setSelectAll: (selectAll) => set({ selectAll }),
  setDeliveryDate: (date) => set({ deliveryDate: date }),
  setCurrentStep: (step) => set({ currentStep: step }),
  
  addSelectedTool: (index) => {
    const { selectedTools, availableTools, unselectedTools } = get();
    const newSelectedTools = selectedTools.includes(index) ? selectedTools : [...selectedTools, index];
    const newUnselectedTools = unselectedTools.filter(i => i !== index);
    set({ 
      selectedTools: newSelectedTools,
      unselectedTools: newUnselectedTools,
      selectAll: newSelectedTools.length === availableTools.length
    });
  },
  
  removeSelectedTool: (index) => {
    const { selectedTools, unselectedTools } = get();
    const newSelectedTools = selectedTools.filter(i => i !== index);
    const newUnselectedTools = unselectedTools.includes(index) ? unselectedTools : [...unselectedTools, index];
    set({ 
      selectedTools: newSelectedTools,
      unselectedTools: newUnselectedTools,
      selectAll: false
    });
  },
  
  toggleToolSelection: (index) => {
    const { selectedTools, availableTools, unselectedTools } = get();
    const isSelected = selectedTools.includes(index);
    const newSelectedTools = isSelected 
      ? selectedTools.filter(i => i !== index)
      : [...selectedTools, index];
    
    const newUnselectedTools = isSelected
      ? unselectedTools.includes(index) ? unselectedTools : [...unselectedTools, index]
      : unselectedTools.filter(i => i !== index);
    
    set({ 
      selectedTools: newSelectedTools,
      unselectedTools: newUnselectedTools,
      selectAll: newSelectedTools.length === availableTools.length
    });
  },
  
  clearSelectedTools: () => {
    const { availableTools } = get();
    const allIndices = availableTools.map((_, index) => index);
    set({ 
      selectedTools: [], 
      unselectedTools: allIndices,
      selectAll: false 
    });
  },
  
  toggleSelectAll: () => {
    const { availableTools, selectAll } = get();
    const allIndices = availableTools.map((_, index) => index);
    
    if (selectAll) {
      set({ 
        selectedTools: [], 
        unselectedTools: allIndices,
        selectAll: false 
      });
    } else {
      set({ 
        selectedTools: allIndices, 
        unselectedTools: [],
        selectAll: true 
      });
    }
  },
  
  getSelectedToolsArray: () => {
    const { availableTools, selectedTools } = get();
    return selectedTools
      .map(index => ({
        tool: availableTools[index],
        originalIndex: index
      }))
      .filter(item => item.tool);
  },
  
  getUnselectedToolsArray: () => {
    const { availableTools, unselectedTools } = get();
    return unselectedTools
      .map(index => ({
        tool: availableTools[index],
        originalIndex: index
      }))
      .filter(item => item.tool);
  },
  
  isToolSelected: (index) => {
    const { selectedTools } = get();
    return selectedTools.includes(index);
  },
  
  getSelectedToolsCount: () => {
    const { selectedTools } = get();
    return selectedTools.length;
  },
  
  resetState: () => set({
    availableTools: [],
    markedNotDeliveredToolIds: [],
    selectedTools: [],
    unselectedTools: [],
    selectAll: false,
    deliveryDate: '',
    currentStep: SECTIONS_STEPS.SELECTION
  })
}));

export default useDeliveredToolsState;
